import { useState, useEffect, useCallback } from 'react';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { getV2Progress, restartV2Setup } from '@/api/v2-deployment';
import { getStepTranslation } from '@/lib/translations/v2-steps';
import type { V2DeploymentInfo, V2SetupStatus, V2StepDefinition, V2StepResult, V2StepStatus } from '@/types/v2-deployment';
import {
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  RefreshCw,
  Loader2,
  MinusCircle,
  Circle,
} from 'lucide-react';

interface V2DeploymentStatusProps {
  schoolCode: string;
  onStatusChange?: (status: V2SetupStatus | null) => void;
  autoRefresh?: boolean;
  refreshInterval?: number;
}

const STATUS_LABELS: Record<V2SetupStatus, string> = {
  not_started: 'Non demarré',
  in_progress: 'En cours',
  completed: 'Terminé',
  failed: 'Échec',
};

const STATUS_COLORS: Record<V2SetupStatus, string> = {
  not_started: 'text-gray-500',
  in_progress: 'text-blue-500',
  completed: 'text-green-500',
  failed: 'text-red-500',
};

function StatusIcon({ status }: { status: V2SetupStatus }) {
  switch (status) {
    case 'completed':
      return <CheckCircle className="h-5 w-5 text-green-500" />;
    case 'failed':
      return <XCircle className="h-5 w-5 text-red-500" />;
    case 'in_progress':
      return <Loader2 className="h-5 w-5 text-blue-500 animate-spin" />;
    default:
      return <Clock className="h-5 w-5 text-gray-500" />;
  }
}

function StepStatusIcon({ status, isCurrentStep }: { status: V2StepStatus; isCurrentStep?: boolean }) {
  if (isCurrentStep && status === 'pending') {
    return <Loader2 className="h-4 w-4 text-blue-500 animate-spin flex-shrink-0" />;
  }
  switch (status) {
    case 'success':
      return <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />;
    case 'failure':
      return <XCircle className="h-4 w-4 text-red-500 flex-shrink-0" />;
    case 'skipped':
      return <MinusCircle className="h-4 w-4 text-gray-400 flex-shrink-0" />;
    case 'pending':
    default:
      return <Circle className="h-4 w-4 text-gray-300 flex-shrink-0" />;
  }
}

function StepItem({
  stepId,
  stepNumber,
  step,
  result,
  isCurrentStep,
}: {
  stepId: string;
  stepNumber: number;
  step: V2StepDefinition;
  result?: V2StepResult;
  isCurrentStep?: boolean;
}) {
  const statusColors: Record<V2StepStatus, string> = {
    pending: isCurrentStep ? 'bg-blue-50 border-blue-200' : 'bg-gray-50 border-gray-200',
    success: 'bg-green-50 border-green-200',
    failure: 'bg-red-50 border-red-200',
    skipped: 'bg-gray-50 border-gray-200',
  };

  // Get translated step name and description
  const translation = getStepTranslation(stepId, step.name, step.description);

  return (
    <div className={`flex items-start gap-3 py-2.5 px-3 rounded-lg border ${statusColors[step.status]}`}>
      <div className="flex items-center gap-2">
        <span className="text-xs font-medium text-gray-400 w-5">{stepNumber}.</span>
        <div className="mt-0.5">
          <StepStatusIcon status={step.status} isCurrentStep={isCurrentStep} />
        </div>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="text-sm font-medium">{translation.name}</p>
          {isCurrentStep && step.status === 'pending' && (
            <span className="text-xs px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded">
              En cours
            </span>
          )}
        </div>
        <p className="text-xs text-gray-500 mt-0.5">{translation.description}</p>
        {result?.message && (
          <p className={`text-xs mt-1 ${result.success ? 'text-green-600' : 'text-red-600'}`}>
            {result.message}
          </p>
        )}
        {result?.executed_at && (
          <p className="text-xs text-gray-400 mt-0.5">
            {new Date(result.executed_at).toLocaleTimeString()}
          </p>
        )}
      </div>
    </div>
  );
}

export function V2DeploymentStatus({
  schoolCode,
  onStatusChange,
  autoRefresh = true,
  refreshInterval = 5000,
}: V2DeploymentStatusProps) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<V2DeploymentInfo | null>(null);
  const [restarting, setRestarting] = useState(false);

  const fetchProgress = useCallback(async () => {
    try {
      const result = await getV2Progress(schoolCode);
      setData(result);
      setError(null);

      if (result.progress?.status) {
        onStatusChange?.(result.progress.status);
      } else if (result.localStatus) {
        onStatusChange?.(result.localStatus);
      }
    } catch (err: unknown) {
      // Handle 404 as "not yet deployed" rather than an error
      const axiosError = err as { response?: { status?: number; data?: { deployed?: boolean } } };
      if (axiosError.response?.status === 404) {
        // Deployment doesn't exist yet, set deployed: false and continue polling
        setData({ deployed: false } as V2DeploymentInfo);
        setError(null);
      } else {
        setError((err as Error).message || 'Erreur lors de la récupération du statut');
      }
    } finally {
      setLoading(false);
    }
  }, [schoolCode, onStatusChange]);

  useEffect(() => {
    fetchProgress();
  }, [fetchProgress]);

  // Auto-refresh when in progress or waiting for deployment to start
  useEffect(() => {
    if (!autoRefresh) return;

    // Continue polling when:
    // 1. Still loading initial data
    // 2. Not yet deployed (waiting for deployment record to be created)
    // 3. Status is not_started (waiting for deployment to begin)
    // 4. Status is in_progress (deployment is running)
    const shouldPoll = loading ||
      !data?.deployed ||
      !data?.progress ||
      data.progress.status === 'not_started' ||
      data.progress.status === 'in_progress';

    if (!shouldPoll) return;

    const interval = setInterval(fetchProgress, refreshInterval);
    return () => clearInterval(interval);
  }, [autoRefresh, loading, data?.deployed, data?.progress?.status, fetchProgress, refreshInterval]);

  const handleRestart = async () => {
    setRestarting(true);
    try {
      await restartV2Setup(schoolCode);
      await fetchProgress();
    } catch (err) {
      setError((err as Error).message || 'Erreur lors du redémarrage');
    } finally {
      setRestarting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-4">
        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
        <span className="ml-2 text-gray-500">Chargement...</span>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertTriangle className="h-4 w-4" />
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  if (!data?.deployed) {
    // If autoRefresh is enabled, we're likely waiting for deployment to be created
    if (autoRefresh) {
      return (
        <div className="flex items-center justify-center p-4">
          <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
          <span className="ml-2 text-gray-500">Initialisation du déploiement V2...</span>
        </div>
      );
    }
    return (
      <div className="text-center p-4 text-gray-500">
        Cette école n'a pas de déploiement V2
      </div>
    );
  }

  const progress = data.progress;
  const status = progress?.status || data.localStatus || 'not_started';
  const completedSteps = progress?.completed_steps ?? 0;
  const totalSteps = progress?.total_steps ?? 0;
  const percentage = progress?.progress_percentage ?? (totalSteps > 0 ? (completedSteps / totalSteps) * 100 : 0);

  return (
    <div className="space-y-4">
      {/* Header with status */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <StatusIcon status={status} />
          <span className={`font-medium ${STATUS_COLORS[status]}`}>
            {STATUS_LABELS[status]}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={fetchProgress}
            disabled={loading}
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
          {status === 'failed' && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleRestart}
              disabled={restarting}
            >
              {restarting ? (
                <Loader2 className="h-4 w-4 animate-spin mr-1" />
              ) : (
                <RefreshCw className="h-4 w-4 mr-1" />
              )}
              Relancer
            </Button>
          )}
        </div>
      </div>

      {/* Progress bar */}
      {progress && (
        <div className="space-y-1">
          <div className="flex justify-between text-sm text-gray-500">
            <span>
              {completedSteps} / {totalSteps} étapes
            </span>
            <span>{Math.round(percentage)}%</span>
          </div>
          <Progress value={percentage} />
          {progress.current_step && status === 'in_progress' && (
            <p className="text-xs text-gray-500">
              Étape en cours: {getStepTranslation(progress.current_step, progress.steps?.[progress.current_step]?.name).name}
            </p>
          )}
        </div>
      )}

      {/* Error message */}
      {progress?.error && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>{progress.error}</AlertDescription>
        </Alert>
      )}

      {/* Steps list */}
      {progress?.steps && Object.keys(progress.steps).length > 0 && (
        <div className="space-y-2 max-h-80 overflow-y-auto">
          {Object.entries(progress.steps).map(([stepId, step], index) => (
            <StepItem
              key={stepId}
              stepId={stepId}
              stepNumber={index + 1}
              step={step}
              result={progress.step_results?.[stepId]}
              isCurrentStep={progress.current_step === stepId}
            />
          ))}
        </div>
      )}

      {/* Tenant info */}
      <div className="text-xs text-gray-400 space-y-0.5">
        <p>Tenant ID: {data.tenantId}</p>
        <p>Groupe: {data.tenantGroupId}</p>
        {data.lastSyncAt && (
          <p>Dernière sync: {new Date(data.lastSyncAt).toLocaleString()}</p>
        )}
      </div>

      {/* Warning */}
      {data.warning && (
        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>{data.warning}</AlertDescription>
        </Alert>
      )}
    </div>
  );
}
